import { Controller } from "@hotwired/stimulus"

const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script"
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
const TURNSTILE_LOAD_INTERVAL_MS = 50
const TURNSTILE_LOAD_TIMEOUT_MS = 10000

let turnstileLoadPromise

const loadTurnstile = () => {
  if (window.turnstile?.render) return Promise.resolve(window.turnstile)
  if (turnstileLoadPromise) return turnstileLoadPromise

  const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID)

  if (!existingScript) {
    const script = document.createElement("script")
    script.id = TURNSTILE_SCRIPT_ID
    script.src = TURNSTILE_SCRIPT_SRC
    script.async = true
    document.head.appendChild(script)
  }

  turnstileLoadPromise = new Promise((resolve, reject) => {
    let elapsed = 0

    const interval = window.setInterval(() => {
      if (window.turnstile?.render) {
        window.clearInterval(interval)
        resolve(window.turnstile)
        return
      }

      elapsed += TURNSTILE_LOAD_INTERVAL_MS

      if (elapsed < TURNSTILE_LOAD_TIMEOUT_MS) return

      window.clearInterval(interval)
      reject(new Error("Timed out loading Cloudflare Turnstile"))
    }, TURNSTILE_LOAD_INTERVAL_MS)
  }).catch((error) => {
    turnstileLoadPromise = undefined
    throw error
  })

  return turnstileLoadPromise
}

export default class extends Controller {
  static values = {
    action: { type: String },
    appearance: { type: String, default: "interaction-only" },
    execution: { type: String, default: "render" },
    responseFieldName: { type: String, default: "cf_turnstile_token" },
    sitekey: String,
    size: { type: String, default: "flexible" },
    theme: { type: String, default: "auto" },
  }

  handleBeforeCache = () => {
    this.#cleanupWidget()
  }

  connect() {
    if (!this.hasSitekeyValue) return

    this.isActive = true
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)
    this.#clearResponseFields()
    this.#setPendingState(true)

    loadTurnstile()
      .then(() => {
        if (!this.isActive) return

        this.#renderWidget()
      })
      .catch((error) => {
        console.error(error)
      })
  }

  disconnect() {
    this.isActive = false
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    this.#cleanupWidget()
  }

  #renderWidget() {
    if (this.widgetId !== undefined) return

    this.#clearResponseFields()
    this.element.innerHTML = ""

    this.widgetId = window.turnstile.render(this.element, {
      action: this.actionValue,
      appearance: this.appearanceValue,
      callback: () => this.#setPendingState(false),
      "error-callback": () => this.#setPendingState(true),
      "expired-callback": () => this.#setPendingState(true),
      execution: this.executionValue,
      "timeout-callback": () => this.#setPendingState(true),
      "response-field": true,
      "response-field-name": this.responseFieldNameValue,
      retry: "auto",
      "refresh-expired": "auto",
      "refresh-timeout": "auto",
      sitekey: this.sitekeyValue,
      size: this.sizeValue,
      theme: this.themeValue,
    })
  }

  #cleanupWidget() {
    if (this.widgetId !== undefined && window.turnstile?.remove) {
      window.turnstile.remove(this.widgetId)
    }

    this.widgetId = undefined
    this.element.innerHTML = ""
    this.#clearResponseFields()
    this.#setPendingState(false)
  }

  #clearResponseFields() {
    this.#form()
      ?.querySelectorAll(`[name="${this.responseFieldNameValue}"]`)
      .forEach((field) => field.remove())
  }

  #dispatchPendingState(pending) {
    this.#form()?.dispatchEvent(
      new CustomEvent("turnstile:state-change", {
        bubbles: true,
        detail: { pending },
      })
    )
  }

  #form() {
    return this.element.closest("form")
  }

  #setPendingState(pending) {
    const form = this.#form()
    if (!form) return

    form.dataset.turnstilePending = String(pending)
    form.querySelectorAll('[type="submit"]').forEach((button) => {
      button.disabled = pending
    })
    this.#dispatchPendingState(pending)
  }
}
